import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';
import dayjs from 'dayjs';

import { useAuth, useProgressStore } from 'src/hooks';

import { changeHistoryUrl } from 'src/utils/urls';

export type PersonHistoryLogDataType = {
  ColumnName: string;
  Id: number;
  NewValue: string | null;
  PersonDetailCategory: string;
  PreviousValue: string | null;
  UpdatedAt: string;
  UpdatedBy: string;
  UpdatedFrom: string;
};
export type PersonHistorytype = {
  data: Array<PersonHistoryLogDataType> | [] | null;
  draw: number;
  error: Error | null;
  isAutoRefreshEnabled: boolean;
  recordsFiltered: number;
  recordsTotal: number;
};

async function fetchPersonHistory({
  token,
  tokenType,
  requestData,
  pageParam,
}: {
  token: string;
  tokenType: string;
  requestData: FormData;
  pageParam: number;
}): Promise<PersonHistorytype> {
  requestData.append('start', `${pageParam}`);
  const response = await fetch(changeHistoryUrl, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  if (response.status === 500) {
    return {
      data: [],
      draw: 0,
      error: null,
      isAutoRefreshEnabled: false,
      recordsFiltered: 0,
      recordsTotal: 0,
    };
  }
  return response.json();
}

function getColumnFromId(id: string): string {
  const idsList = [
    'Category',
    'Field name',
    'Updated from',
    'Previous value',
    'Current value',
    'Updated by',
    'Updated at',
  ];
  const index = idsList.findIndex(item => item === id);
  const columnIndex = index !== -1 ? index + 1 : 0;
  return `${columnIndex}`;
}

const debouncedFetch = pDebounce(fetchPersonHistory, 500);

const drawValue = 0;
const pageLength = 20;

function setPersonHistoryRequestData(
  personId: string | null | undefined,
  columnsSearch: Record<string, string>,
  currentSortItem: { sortKey: string; sortOrder: string },
): FormData {
  console.log('columnsSearch', columnsSearch);
  const formData: any = new URLSearchParams();
  formData.append('draw', `${drawValue + 1}`);
  formData.append('length', `${pageLength}`);
  formData.append('timezoneOffset', `${new Date().getTimezoneOffset()}`);

  formData.append('columns[0][data]', 'PersonId');
  formData.append('columns[0][name]', '');
  formData.append('columns[0][searchable]', 'true');
  formData.append('columns[0][orderable]', 'true');
  formData.append('columns[0][search][regex]', 'false');
  formData.append('columns[0][search][value]', `${personId}`);

  formData.append('columns[1][data]', 'PersonDetailCategory');
  formData.append('columns[1][name]', '');
  formData.append('columns[1][searchable]', 'true');
  formData.append('columns[1][orderable]', 'true');
  formData.append('columns[1][search][regex]', 'false');
  formData.append(
    'columns[1][search][value]',
    columnsSearch['Category'] ? columnsSearch['Category'] : '',
  );

  formData.append('columns[2][data]', 'ColumnName');
  formData.append('columns[2][name]', '');
  formData.append('columns[2][searchable]', 'true');
  formData.append('columns[2][orderable]', 'true');
  formData.append('columns[2][search][regex]', 'false');
  formData.append(
    'columns[2][search][value]',
    columnsSearch['Field name'] ? columnsSearch['Field name'] : '',
  );

  formData.append('columns[3][data]', 'UpdatedFrom');
  formData.append('columns[3][name]', '');
  formData.append('columns[3][searchable]', 'true');
  formData.append('columns[3][orderable]', 'true');
  formData.append('columns[3][search][regex]', 'false');
  formData.append(
    'columns[3][search][value]',
    columnsSearch['Updated from'] ? columnsSearch['Updated from'] : '',
  );

  formData.append('columns[4][data]', 'PreviousValue');
  formData.append('columns[4][name]', '');
  formData.append('columns[4][searchable]', 'true');
  formData.append('columns[4][orderable]', 'true');
  formData.append('columns[4][search][regex]', 'false');
  formData.append(
    'columns[4][search][value]',
    columnsSearch['Previous value'] ? columnsSearch['Previous value'] : '',
  );

  formData.append('columns[5][data]', 'NewValue');
  formData.append('columns[5][name]', '');
  formData.append('columns[5][searchable]', 'true');
  formData.append('columns[5][orderable]', 'true');
  formData.append('columns[5][search][regex]', 'false');
  formData.append(
    'columns[5][search][value]',
    columnsSearch['Current value'] ? columnsSearch['Current value'] : '',
  );

  formData.append('columns[6][data]', 'UpdatedBy');
  formData.append('columns[6][name]', '');
  formData.append('columns[6][searchable]', 'true');
  formData.append('columns[6][orderable]', 'true');
  formData.append('columns[6][search][regex]', 'false');
  formData.append(
    'columns[6][search][value]',
    columnsSearch['Updated by'] ? columnsSearch['Updated by'] : '',
  );

  formData.append('columns[7][data]', 'UpdatedAt');
  formData.append('columns[7][name]', '');
  formData.append('columns[7][searchable]', 'true');
  formData.append('columns[7][orderable]', 'true');
  formData.append('columns[7][search][regex]', 'false');
  formData.append(
    'columns[7][search][value]',
    columnsSearch['Updated at']
      ? dayjs(columnsSearch['Updated at']).format('MM/DD/YYYY')
      : '',
  );

  formData.append(
    'order[0][column]',
    getColumnFromId(currentSortItem?.sortKey),
  );
  formData.append('order[0][dir]', currentSortItem?.sortOrder);

  formData.append('search[value]', '');
  formData.append('search[regex]', 'false');

  return formData;
}

function useGetPersonAuditLogs({
  personId,
  columnsSearch,
  currentSortItem,
  setLastUpdated,
}: {
  personId: any;
  columnsSearch: Record<string, string>;
  currentSortItem: { sortKey: string; sortOrder: string };
  setLastUpdated: any;
}): UseInfiniteQueryResult<PersonHistorytype, unknown> {
  const { token, tokenType } = useAuth();
  const { setLoading } = useProgressStore();
  return useInfiniteQuery(
    ['get_person_history', personId, columnsSearch, currentSortItem],
    ({ pageParam = 0 }) => {
      setLoading('isMutateChangeHistoryLoading', true);
      const requestData = setPersonHistoryRequestData(
        personId,
        columnsSearch,
        currentSortItem,
      );
      return debouncedFetch({
        token,
        tokenType,
        requestData,
        pageParam,
      });
    },
    {
      getNextPageParam: (
        lastPage: PersonHistorytype,
        allPages: Array<PersonHistorytype>,
      ) => {
        if (lastPage !== null && lastPage?.data?.length === pageLength)
          return allPages.length * pageLength;
      },
      getPreviousPageParam: (
        firstPage: PersonHistorytype,
        allPages: Array<PersonHistorytype>,
      ) => {
        if (firstPage !== null && firstPage?.data?.length === pageLength)
          return allPages.length - 1 * pageLength;
      },
      enabled: personId !== undefined && personId !== null && personId !== '',
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      onSuccess: () => {
        setLastUpdated(dayjs().format('HH:mm:ss'));
      },
      onSettled: () => {
        setLoading('isMutateChangeHistoryLoading', false);
      },
    },
  );
}

export { useGetPersonAuditLogs };
